import { useState } from "react";
import { TimePicker, parseTimeInput } from "../atoms/TimePicker";

export type ReminderValue =
  | { type: "before"; minutes: number | null }
  | { type: "at_time"; time: string | null }
  | null;

interface ReminderPickerProps {
  readonly value: ReminderValue;
  readonly onChange: (value: ReminderValue) => void;
  readonly disabled?: boolean;
  readonly className?: string;
}

const BEFORE_PRESETS = [
  { label: "5 min", value: 5 },
  { label: "15 min", value: 15 },
  { label: "30 min", value: 30 },
  { label: "1h", value: 60 },
  { label: "24h", value: 1440 },
] as const;

const TIME_PRESETS = [
  { label: "6h", value: "06:00" },
  { label: "9h", value: "09:00" },
  { label: "12h", value: "12:00" },
  { label: "18h", value: "18:00" },
  { label: "20h", value: "20:00" },
] as const;

function minutesToHHMM(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
}

function hhmmToMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}

function BeforeContent({ value, onChange, disabled }: { value: ReminderValue; onChange: (v: ReminderValue) => void; disabled: boolean }) {
  const isCustom = value?.type === "before" && value.minutes !== null && !BEFORE_PRESETS.some((p) => p.value === value.minutes);
  const [showCustom, setShowCustom] = useState(isCustom);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-1">
        <button
          type="button"
          disabled={disabled}
          onClick={() => { onChange(null); setShowCustom(false); }}
          className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
            value === null || (value.type === "before" && value.minutes === null)
              ? "bg-accent/10 text-accent border-accent/30"
              : "text-ink-muted hover:bg-surface hover:border-border/40 disabled:opacity-40"
          }`}
        >
          Non
        </button>
        {BEFORE_PRESETS.map((preset) => (
          <button
            key={preset.value}
            type="button"
            disabled={disabled}
            onClick={() => { onChange({ type: "before", minutes: preset.value }); setShowCustom(false); }}
            className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
              value?.type === "before" && value.minutes === preset.value && !showCustom
                ? "bg-accent/10 text-accent border-accent/30"
                : "text-ink-muted hover:bg-surface hover:border-border/40 disabled:opacity-40"
            }`}
          >
            {preset.label}
          </button>
        ))}
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            setShowCustom(true);
            const minutes = value?.type === "before" && value.minutes ? value.minutes : 10;
            onChange({ type: "before", minutes });
          }}
          className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
            showCustom
              ? "bg-accent/10 text-accent border-accent/30"
              : "text-ink-muted hover:bg-surface hover:border-border/40 disabled:opacity-40"
          }`}
        >
          Custom
        </button>
      </div>
      {showCustom && !disabled && (
        <div className="flex items-center gap-2">
          <TimePicker
            value={value?.type === "before" && value.minutes ? minutesToHHMM(value.minutes) : "00:10"}
            onChange={(time) => {
              const total = hhmmToMinutes(time);
              if (total > 0) onChange({ type: "before", minutes: total });
            }}
            className="flex-1"
          />
          <span className="text-xs text-ink-muted shrink-0">avant</span>
        </div>
      )}
    </div>
  );
}

function AtTimeContent({ value, onChange, disabled }: { value: ReminderValue; onChange: (v: ReminderValue) => void; disabled: boolean }) {
  const isCustom = value?.type === "at_time" && value.time !== null && !TIME_PRESETS.some((p) => p.value === value.time);
  const [showCustom, setShowCustom] = useState(isCustom);
  const [customInput, setCustomInput] = useState(isCustom && value?.type === "at_time" ? value.time ?? "" : "");

  const handleCustomInput = (raw: string) => {
    setCustomInput(raw);
    const parsed = parseTimeInput(raw);
    if (parsed) {
      onChange({ type: "at_time", time: parsed });
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-1">
        <button
          type="button"
          disabled={disabled}
          onClick={() => { onChange(null); setShowCustom(false); }}
          className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
            value === null || (value.type === "at_time" && value.time === null)
              ? "bg-accent/10 text-accent border-accent/30"
              : "text-ink-muted hover:bg-surface hover:border-border/40 disabled:opacity-40"
          }`}
        >
          Non
        </button>
        {TIME_PRESETS.map((preset) => (
          <button
            key={preset.value}
            type="button"
            disabled={disabled}
            onClick={() => { onChange({ type: "at_time", time: preset.value }); setShowCustom(false); }}
            className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
              value?.type === "at_time" && value.time === preset.value && !showCustom
                ? "bg-accent/10 text-accent border-accent/30"
                : "text-ink-muted hover:bg-surface hover:border-border/40 disabled:opacity-40"
            }`}
          >
            {preset.label}
          </button>
        ))}
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            setShowCustom(true);
            const time = value?.type === "at_time" && value.time ? value.time : "09:00";
            setCustomInput(time);
            onChange({ type: "at_time", time });
          }}
          className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
            showCustom
              ? "bg-accent/10 text-accent border-accent/30"
              : "text-ink-muted hover:bg-surface hover:border-border/40 disabled:opacity-40"
          }`}
        >
          Custom
        </button>
      </div>
      {showCustom && !disabled && (
        <input
          type="text"
          value={customInput}
          onChange={(e) => handleCustomInput(e.target.value)}
          placeholder="HH:MM"
          maxLength={5}
          className="w-full px-3 py-2 text-xs text-ink rounded-md bg-surface border border-border/60 hover:border-accent/50 focus:outline-none focus:border-accent/60 transition-colors font-medium"
        />
      )}
    </div>
  );
}

export function ReminderPicker({ value, onChange, disabled = false, className = "" }: ReminderPickerProps) {
  // Mode initial : dériver de la valeur actuelle ou défaut "before"
  const currentMode = value?.type === "at_time" ? "at_time" : "before";
  const [mode, setMode] = useState<"before" | "at_time">(currentMode);

  const handleModeChange = (newMode: "before" | "at_time") => {
    setMode(newMode);
    onChange(null);
  };

  const isBeforeMode = mode === "before";

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      {/* Mode tabs */}
      <div className="flex gap-1 p-1 bg-surface-alt rounded-lg border border-border/60 overflow-hidden">
        <button
          type="button"
          disabled={disabled}
          onClick={() => handleModeChange("before")}
          className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            isBeforeMode
              ? "bg-accent text-white shadow-sm"
              : "text-ink-muted hover:text-ink hover:bg-surface disabled:opacity-40"
          }`}
        >
          Avant l'échéance
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => handleModeChange("at_time")}
          className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            !isBeforeMode
              ? "bg-accent text-white shadow-sm"
              : "text-ink-muted hover:text-ink hover:bg-surface disabled:opacity-40"
          }`}
        >
          À heure fixe
        </button>
      </div>

      {/* Content based on mode */}
      {isBeforeMode ? (
        <BeforeContent value={value} onChange={onChange} disabled={disabled} />
      ) : (
        <AtTimeContent value={value} onChange={onChange} disabled={disabled} />
      )}
    </div>
  );
}
